import React, { useRef, useState } from 'react';
import {
  FaArrowLeft,
  FaFileAlt,
  FaDollarSign,
  FaLock,
  FaSyncAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminLegalPages.css';

export default function TermsConditions() {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!contentRef.current || downloading) return;

    try {
      setDownloading(true);
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#eef1f7',
        ignoreElements: (element) =>
          element?.classList?.contains('exclude-from-pdf'),
      });

      const imageData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imageWidth = pageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;

      let heightLeft = imageHeight;
      let yPosition = 0;

      pdf.addImage(imageData, 'PNG', 0, yPosition, imageWidth, imageHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        yPosition = heightLeft - imageHeight;
        pdf.addPage();
        pdf.addImage(imageData, 'PNG', 0, yPosition, imageWidth, imageHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('terms-and-conditions.pdf');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="legal-page">
      <header className="legal-header">
        <button
          type="button"
          className="legal-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </button>
        <div className="legal-header-content">
          <h1>Terms &amp; Conditions</h1>
          <p>Rules and guidelines for using the platform.</p>
        </div>
      </header>

      <main className="legal-content" ref={contentRef}>
        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaFileAlt /> Platform Usage
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              By using this workforce management platform, you agree to the following usage guidelines:
            </p>
            <ul className="legal-list">
              <li><strong>Accurate information:</strong> Users must provide truthful and accurate information during registration and throughout platform usage.</li>
              <li><strong>Personal accounts:</strong> Accounts are personal and cannot be shared with other individuals.</li>
              <li><strong>Intended use:</strong> The platform is intended exclusively for workforce management and related business operations.</li>
              <li><strong>Prohibited activities:</strong> Users must not attempt to access unauthorized areas, manipulate data, or engage in any fraudulent activities.</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaFileAlt /> Project Responsibilities
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              Contractors and managers have specific responsibilities when using the platform:
            </p>
            <ul className="legal-list">
              <li><strong>Completing assigned tasks:</strong> Contractors must complete all assigned work according to project specifications and timelines.</li>
              <li><strong>Submitting reports on time:</strong> All work reports, timesheets, and documentation must be submitted within the required timeframe.</li>
              <li><strong>Following workplace safety policies:</strong> All users must comply with relevant workplace health and safety regulations.</li>
              <li><strong>Professional conduct:</strong> Maintain professional communication and behavior when interacting with team members through the platform.</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaDollarSign /> Payment Terms
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              Understanding how payments are processed and managed on the platform:
            </p>
            <ul className="legal-list">
              <li><strong>Payment processing:</strong> Payments are processed according to admin payroll settings and organizational policies.</li>
              <li><strong>Timesheet submission:</strong> Contractors must submit accurate timesheets for all work performed.</li>
              <li><strong>Payment delays:</strong> Payment delays may occur if reports are incomplete or contain inaccurate information.</li>
              <li><strong>Payment disputes:</strong> Any payment discrepancies must be reported within 14 days of payment.</li>
            </ul>
            <div className="legal-note warning">
              <strong>Note:</strong> Payment schedules and rates are determined by your employer and managed through the platform&apos;s payroll system.
            </div>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaLock /> Account Security
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              You are responsible for maintaining the security of your account:
            </p>
            <ul className="legal-list">
              <li><strong>Keep password confidential:</strong> Never share your password with anyone, including managers or system administrators.</li>
              <li><strong>Enable two-factor authentication:</strong> We strongly recommend enabling 2FA for additional account security.</li>
              <li><strong>Report suspicious activity:</strong> Immediately report any unauthorized access or suspicious activity on your account.</li>
              <li><strong>Device security:</strong> Use secure devices and networks when accessing the platform.</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2 className="legal-section-title">
            <FaSyncAlt /> Policy Updates
          </h2>
          <div className="legal-section-body">
            <p className="legal-section-intro">
              We may update these terms and conditions as our platform evolves:
            </p>
            <ul className="legal-list">
              <li><strong>Change notifications:</strong> The company may update these terms from time to time. Users will be notified inside the application.</li>
              <li><strong>Continued use:</strong> Continued use of the platform after changes constitutes acceptance of the updated terms.</li>
              <li><strong>Version history:</strong> Previous versions of terms are archived and available upon request.</li>
            </ul>
            <div className="legal-note">
              <strong>Last Updated:</strong> March 8, 2026
            </div>
          </div>
        </section>

        <button
          type="button"
          className="legal-footer-btn exclude-from-pdf"
          onClick={handleDownloadPdf}
          disabled={downloading}
        >
          {downloading ? 'Preparing PDF...' : 'Download Full Terms Document'}
        </button>
      </main>
    </div>
  );
}
